import { useHistory } from 'react-router-dom';
import { Button, Modal } from 'react-bootstrap';
import { useContext, useState, useEffect } from 'react';
import { faCopy, faShareFromSquare } from '@fortawesome/free-regular-svg-icons';
import AuthContext from '../../../Context/AuthContext';
import AuthService from '../../../Services/AuthService';
import ClientService from '../../../Services/ClientService';
import XpektorButton from '../../../Components/XpektorButton/XpektorButton';
import {
  XpektorForm,
  FormInput,
  FormButton,
} from '../../../Components/XpektorForms/XpektorForms';
import RecipientService from '../../../Services/RecipientService';
import './CreateClientModal.scss';

const gT = (t, key) => t(`modal.create-client${key}`);
const gsT = (t, key) => t(`shared${key}`);

/* eslint-disable react/prop-types */
export default function CreateClientModal({
  handleClose,
  handleNewLink,
  onClientCreated,
  t,
}) {
  const history = useHistory();
  const [authUrl, setAuthUrl] = useState(null);
  const [authState] = useContext(AuthContext);

  const [orgNumberErrorText, setOrgNumberErrorText] = useState(null);
  const [orgNumber, setOrgNumber] = useState('');

  const [enableDataFetch, setEnableDataFetch] = useState(false);
  const [dataFetchErrorText, setDataFetchErrorText] = useState(null);
  const [fetchingData, setFetchingData] = useState(false);
  const [createdClient, setCreatedClient] = useState({});

  const [animatingCopied, setAnimatingCopied] = useState(false);
  const [animatingCopiedCount, setAnimatingCopiedCount] = useState(0);

  function ValidateOrgNumber(org) {
    const validLength = org.length === 10;

    if (!validLength) {
      setOrgNumberErrorText(gT(t, '.error-text.invalid-orgnr'));
    } else {
      setOrgNumberErrorText('');
    }

    setEnableDataFetch(validLength === true);
    return validLength;
  }

  function setOrgNumberInput(newValue) {
    if (newValue.length <= 10) {
      setOrgNumber(newValue);
      ValidateOrgNumber(newValue);
    }
  }

  useEffect(() => {
    const animateCopied = () => {
      const newAnimatingCopiedCount = animatingCopiedCount + 1;
      if (newAnimatingCopiedCount === 2) {
        setAnimatingCopiedCount(0);
        setAnimatingCopied(false);
      } else {
        setAnimatingCopiedCount(newAnimatingCopiedCount);
      }
    };
    const timer = setTimeout(() => animatingCopied && animateCopied(), 1000);
    return () => clearTimeout(timer);
  }, [animatingCopiedCount, animatingCopied]);

  const getCompanyAndCreateClient = async (event) => {
    event.preventDefault();

    setFetchingData(true);
    const companyInfo = await ClientService.getBasicCompanyInfo(orgNumber)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        if (error.response.status === 404) {
          setDataFetchErrorText(gT(t, '.error-text.orgnr-not-found'));
        } else {
          setDataFetchErrorText(gT(t, '.error-text.fetch-company'));
        }
        setFetchingData(false);
        return null;
      });

    if (companyInfo === null) return;

    if (companyInfo.errorCode !== null) {
      setDataFetchErrorText(`${companyInfo.errorCode}`);
      setFetchingData(false);
      return;
    }

    setDataFetchErrorText(null);
    await ClientService.createClient(
      companyInfo.name,
      companyInfo.organizationNumber
    )
      .then((clientResponse) => {
        setCreatedClient(clientResponse.data);
        RecipientService.PostRecipient(
          authState?.user.id,
          clientResponse.data.id
        );

        return AuthService.generateExternalAuthUrlForClient(
          clientResponse.data.id,
          null
        );
      })
      .then((linkResponse) => {
        setAuthUrl(linkResponse.data);
        if (handleNewLink) {
          handleNewLink();
        }
      })
      .catch(() => {
        setAuthUrl(null);
        setCreatedClient({});
        setDataFetchErrorText(gT(t, '.error-text.something-went-wrong'));
      })
      .finally(() => {
        if (onClientCreated) {
          onClientCreated();
        }
        setFetchingData(false);
      });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(authUrl);
  };

  return (
    <Modal
      show
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      className="border-0 create-client-modal"
    >
      <Modal.Header>
        <Modal.Title>{t('modal.create-client.modal-title')}</Modal.Title>
        <Button className="btn-close" bsPrefix="button" onClick={handleClose} />
      </Modal.Header>
      <Modal.Body className="border-0">
        <div className="create-client-modal-content">
          <p className="auth-link-description">
            {gT(t, '.auth-link-description.0')}
            <br />
            <br />
            {gT(t, '.auth-link-description.1')}
          </p>

          <XpektorForm onSubmit={getCompanyAndCreateClient}>
            <FormInput
              label={gT(t, '.form-input-label')}
              type="text"
              value={orgNumber}
              onChange={(e) => setOrgNumberInput(e.target.value)}
              infoText={orgNumberErrorText}
            />
            <FormButton
              text={
                fetchingData
                  ? gsT(t, '.actions.fetching')
                  : gT(t, '.form-btn-text.idle')
              }
              disabled={!enableDataFetch || fetchingData}
            />
          </XpektorForm>
          {(Object.keys(createdClient).length > 0 ||
            dataFetchErrorText !== null) &&
            fetchingData === false && (
              <div
                className={`result-display ${
                  dataFetchErrorText != null ? 'error-result' : 'good-result'
                }`}
              >
                <p className="result-title">
                  {dataFetchErrorText
                    ? gT(t, '.error-text.adding-client')
                    : createdClient.alreadyExists
                    ? gsT(t, '.errors.client-exists')
                    : gT(t, '.result-title.client-added')}
                </p>
                {dataFetchErrorText ? (
                  <p>{dataFetchErrorText}</p>
                ) : (
                  <p>
                    {createdClient.name}
                    <br />
                    {createdClient.organizationNumber}
                  </p>
                )}
              </div>
            )}
          {Object.keys(createdClient).length > 0 &&
            dataFetchErrorText === null &&
            fetchingData === false && (
              <div className="action-row">
                <XpektorButton
                  content={gT(t, '.share-client')}
                  icon={faShareFromSquare}
                  onClick={() =>
                    history.push(
                      `/app/settings/clients?action=clientAssignments&clientId=${createdClient.id}`
                    )
                  }
                  variant="outline"
                />
                {authUrl !== null ? (
                  <>
                    <XpektorButton
                      icon={animatingCopied ? '' : faCopy}
                      content={
                        animatingCopied
                          ? gT(t, '.copied-activationlink')
                          : gT(t, '.copy-activationlink')
                      }
                      variant="outline"
                      onClick={() => {
                        copyLink();
                        setAnimatingCopied(true);
                      }}
                    />
                    <XpektorButton
                      content={gT(t, '.open-in-new-tab')}
                      onClick={() =>
                        window.open(authUrl, '_blank', 'noopener,noreferrer')
                      }
                    />
                  </>
                ) : (
                  ''
                )}
                <XpektorButton
                  content={gT(t, '.to-client')}
                  onClick={() =>
                    history.push(
                      `/app/companyinfo/${createdClient.id}/regulatory`
                    )
                  }
                />
              </div>
            )}
        </div>
      </Modal.Body>
    </Modal>
  );
}
